// Project Funding Intake Specification v1.0 — Q1-Q30 grouped into sections.
// Field keys map 1:1 to backend INTAKE_FIELDS (core/prs_scoring.py).
import { PROJECT_CATEGORIES, PROJECT_STAGES, LAND_OPTIONS } from "./projectQuiz";

const sel = (opts) => opts.map((o) => ({ value: o.id, label: o.label, hint: o.hint }));

export const INTAKE_SECTIONS = [
  {
    id: "basics", title: "Project Basics", pillar: null,
    blurb: "Routes your submission and sets the evidence expectations for your stage.",
    fields: [
      { key: "project_category", q: "Q1", label: "What type of project is this?", type: "select", options: sel(PROJECT_CATEGORIES), required: true },
      { key: "location", q: "Q2", label: "Where is the project located?", type: "text", placeholder: "County + town, e.g. Nakuru County, Naivasha", required: true },
      { key: "description", q: "Q3", label: "Describe the project — what will be built and what will it do?", type: "textarea", required: true },
      { key: "stage", q: "Q4", label: "What stage is the project at?", type: "select", options: sel(PROJECT_STAGES), required: true },
      { key: "pct_complete", q: "Q5", label: "If under construction: what % of the build is complete?", type: "number", showIf: (a) => a.stage === "s5", placeholder: "e.g. 40" },
      { key: "completion_certifier", q: "Q5", label: "Who certified that completion figure?", type: "text", showIf: (a) => a.stage === "s5", placeholder: "e.g. QS firm name" },
    ],
  },
  {
    id: "sponsor", title: "Sponsor & Track Record", pillar: "PR-1",
    blurb: "Can this team deliver this project? Funders back sponsors before assets.",
    fields: [
      { key: "sponsor_entity", q: "Q6", label: "Who is the sponsor/developer, and which entity will own and deliver the project?", type: "textarea", placeholder: "Entity name, registration, shareholding / SPV structure" },
      { key: "comparable_projects", q: "Q7", label: "What comparable projects have you completed? Name, type, size, your role, outcome.", type: "textarea" },
      { key: "delivery_team", q: "Q8", label: "Who is on the delivery team — project manager, engineer/architect, QS, legal, financial advisor?", type: "textarea" },
      { key: "sponsor_funding_plan", q: "Q9", label: "How will the sponsor fund its own share, and can it absorb a reasonable cost overrun?", type: "textarea" },
      { key: "prior_funders", q: "Q10", label: "Which funders have you worked with before? Can you provide two referees?", type: "textarea" },
    ],
  },
  {
    id: "land", title: "Land, Legal & Permits", pillar: "PR-2",
    blurb: "Does the project have the legal right to exist? Contested land is a hard gate.",
    fields: [
      { key: "land_status", q: "Q11", label: "What is the land status?", type: "select", options: sel(LAND_OPTIONS), required: true },
      { key: "land_in_entity_name", q: "Q12", label: "Is the land held in the project entity's name? If not, what's the transfer plan?", type: "textarea" },
      { key: "eia_status", q: "Q13", label: "Environmental approval status (EIA / NEMA)", type: "select", options: [
        { value: "obtained", label: "EIA licence obtained" },
        { value: "in_progress", label: "Application submitted / in progress" },
        { value: "exempt", label: "Category is exempt (documented)" },
        { value: "none", label: "Not started" },
      ] },
      { key: "sector_approvals", q: "Q14", label: "Which sector and county approvals do you hold — e.g. generation licence (EPRA), change of use, construction permit?", type: "textarea" },
      { key: "tax_compliant", q: "Q15", label: "Is the project entity tax compliant with statutory filings up to date?", type: "select", options: [
        { value: "yes", label: "Yes — current KRA tax compliance certificate" },
        { value: "no", label: "No" },
        { value: "unsure", label: "Not sure" },
      ] },
    ],
  },
  {
    id: "technical", title: "Technical Feasibility & Cost", pillar: "PR-3",
    blurb: "Is the project technically sound and is the budget believable?",
    fields: [
      { key: "feasibility_status", q: "Q16", label: "Has a feasibility study been prepared?", type: "select", options: [
        { value: "independent", label: "Yes — by an independent named firm" },
        { value: "internal", label: "Yes — prepared internally" },
        { value: "none", label: "Not yet" },
      ] },
      { key: "feasibility_author", q: "Q16", label: "Who authored the feasibility study?", type: "text", showIf: (a) => a.feasibility_status === "independent" || a.feasibility_status === "internal" },
      { key: "total_cost", q: "Q17", label: "Total project cost", type: "number", required: true, placeholder: "e.g. 800000000" },
      { key: "cost_currency", q: "Q17", label: "Currency", type: "select", options: [
        { value: "KES", label: "KES" }, { value: "USD", label: "USD" },
        { value: "NGN", label: "NGN" }, { value: "GHS", label: "GHS" },
        { value: "TZS", label: "TZS" }, { value: "UGX", label: "UGX" },
      ] },
      { key: "cost_breakdown", q: "Q17", label: "Break the cost into major line items — what's the basis of the estimates?", type: "textarea" },
      { key: "contingency_pct", q: "Q17", label: "Contingency included (% of hard costs)", type: "number", placeholder: "Typically 5–10" },
      { key: "contractor_arrangement", q: "Q18", label: "Who will build it, and under what contract form?", type: "select", options: [
        { value: "fixed_price", label: "Fixed-price turnkey EPC" },
        { value: "cost_plus", label: "Cost-plus contract" },
        { value: "self_build", label: "Self-build / owner-managed" },
        { value: "none", label: "Not yet decided" },
      ] },
      { key: "contractor_name", q: "Q18", label: "Named contractor / EPC firm", type: "text" },
      { key: "design_maturity", q: "Q19", label: "What stage is the design at, and is the technology proven at this scale?", type: "textarea" },
    ],
  },
  {
    id: "revenue", title: "Revenue Certainty & Offtake", pillar: "PR-4",
    blurb: "Who pays, and how much of it is already committed? The biggest fundability differentiator.",
    fields: [
      { key: "offtakers", q: "Q20", label: "Who buys the output or pays the revenue once the project is running?", type: "textarea", placeholder: "Name the offtakers / buyers / tenants" },
      { key: "contracted_pct", q: "Q21", label: "What % of projected stabilised revenue is already contracted? (0 is a valid answer — blank is not)", type: "number", required: true, placeholder: "0–100" },
      { key: "contract_tenor", q: "Q22", label: "How long do those contracts run, relative to the financing term you're seeking?", type: "textarea" },
      { key: "market_evidence", q: "Q23", label: "What evidence supports the revenue not yet contracted — demand study, comparables, price benchmarks?", type: "textarea" },
    ],
  },
  {
    id: "capital", title: "Capital Structure & Viability", pillar: "PR-5",
    blurb: "Does the funding plan work? Sponsor equity, full stack, DSCR / IRR.",
    fields: [
      { key: "invested_to_date", q: "Q24", label: "How much has already been invested in the project?", type: "number", required: true },
      { key: "invested_form", q: "Q24", label: "In what form — cash, land, or work in place? By whom?", type: "textarea" },
      { key: "amount_sought", q: "Q25", label: "How much are you raising now?", type: "number", required: true },
      { key: "capital_type", q: "Q25", label: "What type of capital?", type: "select", required: true, options: [
        { value: "debt_bank", label: "Bank debt" },
        { value: "debt_dfi", label: "DFI / development debt" },
        { value: "equity", label: "Equity / development capital" },
        { value: "grant", label: "Grant / blended finance" },
        { value: "mixed", label: "A mix (debt + equity)" },
      ] },
      { key: "other_sources", q: "Q26", label: "What other funding sources complete the total project cost, and are they committed?", type: "textarea" },
      { key: "projected_dscr", q: "Q27", label: "Projected DSCR at stabilisation (for debt)", type: "number", placeholder: "e.g. 1.4", showIf: (a) => ["debt_bank", "debt_dfi", "mixed"].includes(a.capital_type) },
      { key: "projected_irr", q: "Q27", label: "Projected project IRR % (for equity)", type: "number", placeholder: "e.g. 22", showIf: (a) => ["equity", "grant", "mixed"].includes(a.capital_type) },
      { key: "construction_period_months", q: "Q27", label: "Construction period (months)", type: "number" },
      { key: "first_revenue_date", q: "Q27", label: "Date of first revenue", type: "text", placeholder: "e.g. Q3 2027" },
    ],
  },
  {
    id: "risk", title: "Risk, Security & Compliance", pillar: "PR-6",
    blurb: "What protects the funder when things go wrong? Full disclosure — searches are run.",
    fields: [
      { key: "encumbrances", q: "Q28", label: "Any existing loans, charges, caveats or disputes over the project, the land, or the entity?", type: "textarea", required: true, placeholder: "Write 'None' if there are none — full disclosure is verified against official searches" },
      { key: "security_offered", q: "Q29", label: "What security can you offer — charge over land, debenture, assignment of receivables, sponsor guarantees?", type: "textarea" },
      { key: "insurance_strategy", q: "Q30", label: "How are construction and operational risks insured?", type: "textarea" },
      { key: "esg_risks", q: "Q30", label: "Community / ESG risks and their mitigation", type: "textarea" },
      { key: "fx_mismatch", q: "Q30", label: "Are your costs and revenues in different currencies?", type: "select", options: [
        { value: "no", label: "No — same currency" },
        { value: "yes", label: "Yes — mismatched (mitigation described above)" },
        { value: "na", label: "Not applicable" },
      ] },
    ],
  },
];
